import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Audit, AuditRequest } from '../../types/AuditTypes';
import { Helmet } from 'react-helmet-async';
import axios from '../../../services/ApiService';
import LoadingSpinner from '../../support/LoadingSpinner';

export default function AgentActivities(): React.JSX.Element {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(false);
  
  const fetchActivities = async () => {
    const filters = {
      role: '',
      userId: selectedUser ? selectedUser.id : undefined,
      timestamp: selectedDate,
    };
    setAudits([]);
    setLoading(true);
    try {
      const response = await axios.post('/getAgentActivities', filters);
      if (Array.isArray(response.data)) {
        setAudits(response.data);
        
        const userMap = new Map();
        response.data.forEach((audit: Audit) => {
          if (audit.userId && audit.userName) {
            userMap.set(audit.userId, audit.userName);
          }
        });
        
        const userArray = Array.from(userMap, ([id, name]) => ({ id, name }));
        setUsers(userArray);
      }
    } catch (error) {
      console.error('Error fetching activities.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [selectedUser, selectedDate]);

  const handleUserChange = (event: any, newValue: any) => {
    setSelectedUser(newValue);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  const handleSortOrderChange = () => {
    setSortOrder((prevSortOrder) => (prevSortOrder === 'asc' ? 'desc' : 'asc'));
  };

  const sortedActivities = [...audits].sort((a, b) => {
    if (sortOrder === 'asc') {
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    } else {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    }
  });

  return (
    <>
      <Helmet><title>BVT Agent - Activities</title></Helmet>
      <Card sx={{ width: '100%' }}>
        <CardHeader title="Recent Activities" subheader="View Business Activities" />
        <Divider />
        <CardContent>
          <Stack spacing={3} sx={{ width: '100%' }}>
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <TextField
                label="Date"
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Autocomplete
                options={users}
                getOptionLabel={(user) => user.name}
                value={selectedUser}
                onChange={handleUserChange}
                fullWidth
                renderInput={(params) => <TextField {...params} label="User" />}
              />
            </Stack>

            <Box mt={4}>
              <TableContainer>
                <Table sx={{ width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active
                          direction={sortOrder}
                          onClick={handleSortOrderChange}
                        >
                          Timestamp
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography variant="body1" color="textSecondary">
                            {loading ? '' : 'No activity recorded for selected criteria'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedActivities.map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell>{activity.timestamp}</TableCell>
                          <TableCell>{activity.event}</TableCell>
                          <TableCell>{activity.entityType}</TableCell>
                          <Tooltip title={activity.details}>
                            <TableCell>{activity.action}</TableCell>
                          </Tooltip>
                          <TableCell>{activity.userName}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <LoadingSpinner loading={loading} />
    </>
  );
}
